import React, { useMemo } from "react";
import { Box, Button, Typography, Divider, Link } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { replaceAll } from "app/utils/stringHelpers";
import { DSOK_FE_DOMAIN } from "app/utils/constants/settings";

const FALLBACK = "-";

function safeStr(val) {
  if (val == null) return FALLBACK;
  const s = String(val).trim();
  return s === "" ? FALLBACK : s;
}

function parseDsokSettings(item) {
  if (!item) return null;
  try {
    const raw = item.dsokSettings;
    if (raw == null) return null;
    if (typeof raw === "object") return raw;
    if (typeof raw === "string" && raw !== "null") return JSON.parse(raw);
  } catch (_) {
    // ignore
  }
  return null;
}

function getFirstLocation(dsok) {
  const locs = dsok?.customer?.locations;
  if (!locs || typeof locs !== "object") return null;
  const key = Object.keys(locs)[0];
  return key != null ? locs[key] : null;
}

function getDemoDevice(dsok) {
  const devices = dsok?.customer?.devices;
  if (!devices || typeof devices !== "object") return null;
  const entry = Object.entries(devices).find(
    ([, d]) => d && d.demoMode === true,
  );
  return entry ? { key: entry[0], device: entry[1] } : null;
}

function buildPreviewUrl(item, dsok, demoDevice) {
  if (!demoDevice || !dsok?.customer) return null;
  const customerId = item?.id ?? item?.customerId;
  const deviceUUID = demoDevice.device?.uuid;
  if (!deviceUUID) return null;
  const code = dsok.customer.code ?? dsok.customer.storeCode ?? "";
  const defaultLanguage = dsok.customer.defaultLanguage ?? "sk";
  const beDomain = dsok.customer.beDomain ?? "";
  const frontendType = dsok.customer.frontendType ?? "";

  const isStaging =
    typeof beDomain === "string" && beDomain.toLowerCase().includes("staging");

  switch (frontendType) {
    case "easyPay":
      return `https://easypay.gastrokiosk.online/?customerId=${customerId}&deviceId=${deviceUUID}`;
    case "smartKiosk":
    case "smartKioskStaging": // legacy, same as smartKiosk (preview by beDomain)
      return isStaging
        ? `https://dsok-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`
        : `https://dsok.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`;
    case "easyFood":
      return isStaging
        ? `https://easyfood-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`
        : `https://easyfood.smartkiosk.sk/?customerId=${customerId}&deviceId=${deviceUUID}`;
    default: {
      const subdomain = replaceAll(code, "-", "");
      if (!subdomain) return null;
      if (defaultLanguage === "sk") {
        return `https://${subdomain}.${DSOK_FE_DOMAIN}/?kioskId=${deviceUUID}`;
      }
      return `https://${subdomain}.${DSOK_FE_DOMAIN}/${defaultLanguage}/?kioskId=${deviceUUID}`;
    }
  }
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle1"
        fontWeight={600}
        color="text.secondary"
        gutterBottom
      >
        {title}
      </Typography>
      <Box sx={{ pl: 0 }}>{children}</Box>
    </Box>
  );
}

function Row({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        gap: 2,
        mb: 1,
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ textAlign: "right", wordBreak: "break-word" }}
      >
        {value == null || String(value).trim() === ""
          ? FALLBACK
          : String(value).trim()}
      </Typography>
    </Box>
  );
}

const DsokCustomerPreviewModal = ({ customer: item, onClose }) => {
  const {
    company,
    location,
    customerCode,
    locationCode,
    locationSection,
    envLabel,
    webviewUrl,
  } = useMemo(() => {
    const dsok = parseDsokSettings(item);
    const loc = getFirstLocation(dsok);
    const demoDevice = getDemoDevice(dsok);

    const companyName =
      item?.companyName ?? item?.company_name ?? loc?.name ?? FALLBACK;
    const ico = loc?.ico ?? item?.ico ?? FALLBACK;
    const dic = loc?.dic ?? item?.dic ?? FALLBACK;
    const street = loc?.street ?? item?.street ?? FALLBACK;
    const mesto = loc?.city ?? item?.city ?? FALLBACK;
    const psc = loc?.postcode ?? item?.zip ?? item?.postcode ?? FALLBACK;
    const stat = loc?.country_id ?? item?.country ?? FALLBACK;

    const customerCodeVal =
      dsok?.customer?.code ??
      dsok?.customer?.storeCode ??
      item?.storeCode ??
      FALLBACK;
    const locationCodeVal = loc?.code ?? FALLBACK;

    const locName = loc?.name ?? FALLBACK;
    const locStreet = loc?.street ?? FALLBACK;
    const locCity = loc?.city ?? FALLBACK;
    const locPsc = loc?.postcode ?? FALLBACK;
    const fullName =
      [loc?.firstname, loc?.lastname].filter(Boolean).join(" ") || FALLBACK;
    const email = loc?.email ?? FALLBACK;
    const telephone = loc?.telephone ?? FALLBACK;

    const beDomain = dsok?.customer?.beDomain ?? "";
    const envLabelVal =
      typeof beDomain === "string" && beDomain.toLowerCase().includes("staging")
        ? "DEV"
        : "PROD";

    const webviewUrlVal = buildPreviewUrl(item, dsok, demoDevice);

    return {
      company: {
        companyName: safeStr(companyName),
        ico: safeStr(ico),
        dic: safeStr(dic),
        street: safeStr(street),
        mesto: safeStr(mesto),
        psc: safeStr(psc),
        stat: safeStr(stat),
      },
      location: loc,
      customerCode: safeStr(customerCodeVal),
      locationCode: safeStr(locationCodeVal),
      locationSection: {
        name: safeStr(locName),
        street: safeStr(locStreet),
        city: safeStr(locCity),
        postcode: safeStr(locPsc),
        fullName,
        email: safeStr(email),
        telephone: safeStr(telephone),
      },
      envLabel: envLabelVal,
      webviewUrl: webviewUrlVal,
    };
  }, [item]);

  return (
    <Box
      sx={{
        maxHeight: "70vh",
        overflow: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Section title="Informácie o spoločnosti">
        <Row label="Názov firmy" value={company.companyName} />
        <Row label="IČO" value={company.ico} />
        <Row label="DIČ" value={company.dic} />
        <Row label="Ulica" value={company.street} />
        <Row label="Mesto" value={company.mesto} />
        <Row label="PSČ" value={company.psc} />
        <Row label="Štát" value={company.stat} />
      </Section>

      <Divider sx={{ my: 2 }} />

      <Section title="Nastavenie zákazníka">
        <Row label="customerCode" value={customerCode} />
        <Row label="locationCode" value={locationCode} />
      </Section>

      <Divider sx={{ my: 2 }} />

      <Section title="Prevádzka">
        <Row label="Názov prevádzky (lokality)" value={locationSection.name} />
        <Row label="Ulica" value={locationSection.street} />
        <Row label="Mesto" value={locationSection.city} />
        <Row label="PSČ" value={locationSection.postcode} />
        <Row label="Meno a priezvisko" value={locationSection.fullName} />
        <Row label="Email" value={locationSection.email} />
        <Row label="Telefón" value={locationSection.telephone} />
      </Section>

      <Divider sx={{ my: 2 }} />

      <Section title="Ostatné nastavenia">
        <Row label="Prostredie:" value={envLabel} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            mb: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Webview:
          </Typography>
          <Box sx={{ textAlign: "right", wordBreak: "break-all" }}>
            {webviewUrl != null && webviewUrl !== "" ? (
              <Link
                href={webviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {webviewUrl}
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </Link>
            ) : (
              <Typography variant="body2">Nie je dostupné</Typography>
            )}
          </Box>
        </Box>
      </Section>

      <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={onClose}>
          Zavrieť
        </Button>
      </Box>
    </Box>
  );
};

export default DsokCustomerPreviewModal;
